import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "./tokens";

const useStyles = makeStyles({
  root: {
    position: "relative",
    width: "44px",
    height: "44px",
    flexShrink: 0,
  },
  svg: {
    transform: "rotate(-90deg)",
  },
  label: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
  },
  labelLight: {
    color: BRAND.ink,
  },
  labelDark: {
    color: BRAND.white,
  },
});

type Props = {
  /** 0..1 */
  value: number;
  ariaLabel?: string;
  /** Adapts the track + label to a dark background. */
  dark?: boolean;
};

const SIZE = 44;
const STROKE = 4;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

const CircularProgress = ({ value, ariaLabel, dark = false }: Props) => {
  const styles = useStyles();
  const clamped = Math.max(0, Math.min(1, value));
  const offset = CIRC * (1 - clamped);
  const pct = Math.round(clamped * 100);
  const trackColor = dark ? "rgba(255,255,255,0.18)" : "rgba(45,20,43,0.12)";
  return (
    <div
      className={styles.root}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={ariaLabel ?? `Fortschritt ${pct}%`}
    >
      <svg className={styles.svg} width={SIZE} height={SIZE}>
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          stroke={trackColor}
          strokeWidth={STROKE}
          fill="none"
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          stroke={BRAND.gold}
          strokeWidth={STROKE}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 240ms ease" }}
        />
      </svg>
      <span className={mergeClasses(styles.label, dark ? styles.labelDark : styles.labelLight)}>
        {pct}%
      </span>
    </div>
  );
};

export default CircularProgress;
